'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'

import { type PublicUser, toPublicUserFromJson } from '@/lib/common/publicUser'
import { signOutAction } from '@/app/(frontend)/actions'
import { useRouter } from 'next/navigation'
import { createClientLogger } from '@/lib/logger/client'

const log = createClientLogger('UserProvider')

export interface UserContextType {
  /**
   * Deliberately a narrow DTO rather than the full `User` document: this provider is a client
   * component, so whatever it holds is serialised into every authenticated page's RSC payload
   * (issue 2201). Widen `PublicUser` on purpose if the client genuinely needs another field.
   */
  user: PublicUser | null
  /**
   * True while the user is still being fetched in the browser. Only happens on public pages,
   * which are cached and so can't carry the visitor's user in their HTML; elsewhere the server
   * hands the user over and this is always false.
   */
  isLoading: boolean
  signOut: () => Promise<void>
  signIn: (email: string, password: string) => Promise<PublicUser | null>
  refreshUser: () => Promise<void>
}

const initialContext: UserContextType = {
  user: null,
  isLoading: false,
  signOut: () => Promise.resolve(),
  signIn: (email: string, password: string) => Promise.resolve(null),
  refreshUser: () => Promise.resolve(),
}

const UserContext = createContext(initialContext)

export const UserProvider = ({
  children,
  user: _user,
}: {
  children: React.ReactNode
  /** `undefined` means "not known on the server": the provider loads it from /api/auth/session. */
  user: PublicUser | null | undefined
}) => {
  const loadOnClient = _user === undefined
  const [user, setUser] = useState<PublicUser | null>(_user ?? null)
  const [isLoading, setIsLoading] = useState(loadOnClient)
  const router = useRouter()

  useEffect(() => {
    if (!loadOnClient) return
    let cancelled = false
    fetch('/api/auth/session', { credentials: 'include', cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : { user: null }))
      .then((json) => {
        // A sign-in that finished first wins over this (older) answer.
        if (!cancelled) setUser((current) => current ?? toPublicUserFromJson(json.user))
      })
      .catch((error) => log.error({ error }, 'Failed to load session user'))
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [loadOnClient])

  async function signIn(email: string, password: string) {
    const response = await fetch('/api/users/login', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    })
    const json = await response.json()
    // Narrowed like the server-rendered prop, so client state is one shape regardless of origin.
    const signedInUser = toPublicUserFromJson(json.user)
    setUser(signedInUser)

    // Check for pending group invites after successful login
    if (signedInUser) {
      checkPendingInvite()
    }

    return signedInUser
  }

  async function signOut() {
    try {
      // Call Payload CMS logout endpoint directly
      const response = await fetch('/api/users/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok) {
        log.warn({ status: response.status }, 'Logout request failed')
      }
    } catch (error) {
      log.error({ error }, 'Error during logout')
    }
    
    // Clear user state regardless of API response
    setUser(null)
    
    // Redirect to home page instead of login
    router.push('/')
  }

  async function refreshUser() {
    try {
      const response = await fetch('/api/users/me', {
        method: 'GET',
        credentials: 'include',
      })
      if (response.ok) {
        const json = await response.json()
        const refreshedUser = toPublicUserFromJson(json.user)
        setUser(refreshedUser)

        // Check for pending group invites after successful refresh
        if (refreshedUser) {
          checkPendingInvite()
        }
      }
    } catch (error) {
      log.error({ error }, 'Failed to refresh user')
    }
  }

  async function checkPendingInvite() {
    try {
      const response = await fetch('/api/user-groups/invites/check-pending', {
        method: 'POST',
        credentials: 'include',
      })

      if (response.ok) {
        const result = await response.json()

        // If invite was accepted, redirect to the group page
        if (result.accepted && result.redirectTo) {
          router.push(result.redirectTo)
        }
      }
    } catch (error) {
      log.error({ error }, 'Failed to check pending invite')
    }
  }

  return (
    <UserContext.Provider value={{ user, isLoading, signOut, signIn, refreshUser }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUserContext = (): UserContextType => useContext(UserContext)
